import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Wallet, TrendingUp, Target, Activity, BarChart3, Trophy, ArrowUpRight, Bot, RefreshCw,
} from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "../lib/api";
import { useMode } from "../context/ModeContext";
import { useAuth } from "../context/AuthContext";
import { usd, signed, pct, clsx } from "../lib/format";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

function Stat({ icon: Icon, label, value, sub, tone = "default", testid }) {
  const toneCls = tone === "pos" ? "text-pos" : tone === "neg" ? "text-neg" : "text-white";
  return (
    <div data-testid={testid} className="rounded-xl bg-fx-s1 border border-white/5 p-5 hover:border-white/10 transition-colors">
      <div className="flex items-center justify-between">
        <span className="text-xs text-fx-s3 uppercase tracking-wider">{label}</span>
        <Icon className="h-4 w-4 text-fx-s3" />
      </div>
      <div className={clsx("font-mono text-2xl font-bold mt-3", toneCls)}>{value}</div>
      {sub && <div className="text-xs text-fx-s3 mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { mode, account } = useMode();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const [range, setRange] = useState("week");
  const [traffic, setTraffic] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/dashboard/stats");
    setStats(data);
  }, []);

  useEffect(() => { load(); }, [load, mode]);
  useEffect(() => { if (isAdmin) api.get(`/admin/analytics?rng=${range}`).then((r) => setTraffic(r.data)).catch(() => {}); }, [isAdmin, range]);

  const resetDemo = async () => {
    await api.post("/wallet/demo/reset");
    toast.success("Demo account reset to starting balance");
    load();
  };

  if (!stats) return <div className="p-8 text-fx-s3">Loading dashboard…</div>;

  const curve = (stats.equity_curve || []).map((p, i) => ({ i, pnl: p.pnl }));

  return (
    <div className="p-4 lg:p-8 space-y-6 max-w-[1400px]">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Welcome back, {user?.name?.split(" ")[0]}</h1>
          <p className="text-fx-s3 mt-1 flex items-center gap-2">
            <span className={clsx("h-2 w-2 rounded-full pulse-dot", mode === "demo" ? "bg-brandblue" : "bg-gold")} />
            You're viewing your <span className="text-white font-semibold uppercase">{mode}</span> account
          </p>
        </div>
        <div className="flex gap-2">
          {mode === "demo" && <Button variant="outline" onClick={resetDemo} data-testid="reset-demo-btn" className="border-white/10 bg-fx-s1 gap-2"><RefreshCw className="h-4 w-4" /> Reset Demo</Button>}
          <Link to="/terminal"><Button data-testid="open-terminal-btn" className="bg-brandblue hover:bg-blue-500 gap-2">Open Terminal <ArrowUpRight className="h-4 w-4" /></Button></Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Wallet} label="Total Balance" value={usd(stats.balance)} sub={`${mode.toUpperCase()} wallet`} testid="stat-balance" />
        <Stat icon={TrendingUp} label="Today's P/L" value={signed(stats.today_pnl)} tone={stats.today_pnl >= 0 ? "pos" : "neg"} testid="stat-today-pnl" />
        <Stat icon={Target} label="Win Rate" value={`${stats.win_rate}%`} sub={`${stats.winning_trades}W / ${stats.losing_trades}L`} testid="stat-winrate" />
        <Stat icon={BarChart3} label="Total P/L" value={signed(stats.total_pnl)} tone={stats.total_pnl >= 0 ? "pos" : "neg"} sub={`ROI ${pct(stats.roi)}`} testid="stat-total-pnl" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl bg-fx-s1 border border-white/5 p-5">
          {isAdmin ? (
            <div data-testid="admin-traffic-panel">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="font-display font-bold text-lg">Traffic &amp; Platform Activity</h2>
                <div className="flex gap-1">
                  {["today", "week", "month", "year"].map((r) => (
                    <button key={r} data-testid={`traffic-range-${r}`} onClick={() => setRange(r)} className={clsx("px-2.5 py-1 rounded text-xs capitalize", range === r ? "bg-brandblue text-white" : "bg-fx-base text-fx-s3 hover:text-white")}>{r}</button>
                  ))}
                </div>
              </div>
              {!traffic ? (
                <div className="h-56 grid place-items-center text-fx-s3 text-sm">Loading traffic…</div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                    {[["Visitors", traffic.visitors], ["Page views", traffic.pageviews], ["Sessions", traffic.sessions], ["New", traffic.new_visitors], ["Returning", traffic.returning_visitors], ["Registrations", traffic.registrations ?? "—"]].map(([l, v]) => (
                      <div key={l} className="rounded-lg bg-fx-base border border-white/5 p-3">
                        <div className="text-[11px] text-fx-s3 uppercase tracking-wider">{l}</div>
                        <div className="font-mono text-xl font-bold mt-1">{v}</div>
                      </div>
                    ))}
                  </div>
                  <ResponsiveContainer width="100%" height={160}>
                    <AreaChart data={traffic.timeseries || []}>
                      <defs><linearGradient id="tg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3B82F6" stopOpacity={0.5} /><stop offset="100%" stopColor="#3B82F6" stopOpacity={0} /></linearGradient></defs>
                      <XAxis dataKey="day" tick={{ fill: "#97A3B6", fontSize: 11 }} />
                      <YAxis hide />
                      <Tooltip contentStyle={{ background: "#1C1F26", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} />
                      <Area type="monotone" dataKey="views" stroke="#3B82F6" strokeWidth={2} fill="url(#tg)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </>
              )}
            </div>
          ) : (
          <>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg">Equity Curve</h2>
            <span className="text-xs text-fx-s3 font-mono">{stats.closed_trades} closed trades</span>
          </div>
          {curve.length === 0 ? (
            <div className="h-56 grid place-items-center text-fx-s3 text-sm">Place your first trade to build your equity curve.</div>
          ) : (
            <ResponsiveContainer width="100%" height={224}>
              <AreaChart data={curve}>
                <defs>
                  <linearGradient id="pnlg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="i" hide />
                <YAxis hide domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: "#1C1F26", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} formatter={(v) => [signed(v), "Cumulative P/L"]} />
                <Area type="monotone" dataKey="pnl" stroke="#3B82F6" strokeWidth={2} fill="url(#pnlg)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
          </>
          )}
        </div>

        <div className="space-y-4">
          <div className="rounded-xl bg-fx-s1 border border-white/5 p-5 grid grid-cols-2 gap-4">
            <div><div className="text-xs text-fx-s3 uppercase">Open</div><div className="font-mono text-xl font-bold flex items-center gap-1"><Activity className="h-4 w-4 text-brandblue" />{stats.open_trades}</div></div>
            <div><div className="text-xs text-fx-s3 uppercase">Total Trades</div><div className="font-mono text-xl font-bold">{stats.total_trades}</div></div>
            <div><div className="text-xs text-fx-s3 uppercase">Week P/L</div><div className={clsx("font-mono text-xl font-bold", stats.week_pnl >= 0 ? "text-pos" : "text-neg")}>{signed(stats.week_pnl)}</div></div>
            <div><div className="text-xs text-fx-s3 uppercase">Month P/L</div><div className={clsx("font-mono text-xl font-bold", stats.month_pnl >= 0 ? "text-pos" : "text-neg")}>{signed(stats.month_pnl)}</div></div>
          </div>
          <Link to="/bots" className="block rounded-xl bg-gradient-to-br from-brandblue/20 to-fx-s1 border border-brandblue/20 p-5 hover:border-brandblue/40 transition-colors">
            <div className="flex items-center gap-2 text-brandblue"><Bot className="h-5 w-5" /><span className="font-display font-bold">AI Trading Bots</span></div>
            <p className="text-sm text-fx-s3 mt-2">Explore automated strategies and test them in demo mode.</p>
          </Link>
        </div>
      </div>
    </div>
  );
}
